<template>
    <Page :header="header[lang_].name">
        <template #body>
            <div class="w-full">                
                <div v-if="bill" class="w-full flex flex-column xl:flex-row lg:flex-row md:flex-row" >
                    <div class="w-full flex flex-column">
                        <div v-if="bill.recipient" class="w-full flex flex-column xl:flex-row relative mb-5">
                            <div class="w-full flex flex-column xl:max-w-30rem lg:max-w-30rem md:max-w-30rem relative surface-100 border-round-xl xl:border-round-2xl p-3 xl:p-4 mb-1 xl:mb-5">
                                <span class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                                    {{ content[lang_].amount }}
                                </span>
                                <span v-if="bill.amount && bill.recipient.currency_code" class="flex text-2xl xl:text-3xl font-semibold text-900 mb-3 xl:mb-4">
                                    <span>{{ get_format_amount(bill.amount.$numberDecimal, bill.recipient.currency_code, 2) }}</span>
                                </span>
                                <span v-else class="flex text-2xl xl:text-3xl font-semibold text-900 mb-3 xl:mb-4">
                                    {{ content[lang_].noAmount }}
                                </span>
                                <span v-if="bill.recipient.fields && bill.recipient.fields.fullname" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                                    {{ content[lang_].fullname }}: <span class="font-medium text-900 ml-1">{{ bill.recipient.fullname }}</span>
                                </span>
                                <span v-if="bill.recipient.bank_code" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                                    {{ content[lang_].bank_code }}: <span class="font-medium text-900 ml-1">{{ get_bank_title(bill.recipient.bank_code) }}</span>
                                </span>
                                <div v-if="bill.recipient.fields" class=w-full>
                                    <span v-if="bill.recipient.fields.account_number" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                                        {{ content[lang_].account_number }}: <span class="font-medium text-900 ml-1">{{ bill.recipient.fields.account_number }}</span>
                                    </span>
                                    <span v-if="bill.recipient.fields.card_number" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                                        {{ content[lang_].account_number }}: <span class="font-medium text-900 ml-1">{{ bill.recipient.fields.card_number }}</span>
                                    </span>
                                    <span v-if="bill.recipient.fields.phone_number" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                                        {{ content[lang_].phone_number }}: <span class="font-medium text-900 ml-1">{{ bill.recipient.fields.phone_number }}</span>
                                    </span>
                                    <span v-if="bill.recipient.fields.wallet_address" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                                        {{ content[lang_].wallet_address }}: <span class="font-medium text-900 ml-1">{{ bill.recipient.fields.wallet_address }}</span>
                                    </span>
                                    <span v-if="bill.recipient.fields.wallet_network" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                                        {{ content[lang_].wallet_network }}: <span class="font-medium text-900 ml-1">{{ bill.recipient.fields.wallet_network }}</span>
                                    </span>
                                    <span v-if="bill.recipient.fields.telegram" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                                        {{ content[lang_].telegram }}: <span class="font-medium text-900 ml-1">{{ bill.recipient.fields.telegram }}</span>
                                    </span>
                                </div>
                                <span v-if="bill.description" class="flex text-sm xl:text-lg text-600 mb-2 xl:mb-3">
                                    {{ content[lang_].description }}: <span class="font-medium text-900 ml-1">{{ bill.description }}</span>
                                </span>
                                <span v-if="bill.hash" class="flex text-sm xl:text-lg text-600 white-space-nowrap">
                                    {{ content[lang_].link }}: <span class="font-medium text-900 ml-1 overflow-hidden ">{{get_link(bill.hash) }}</span>
                                    <span class="pi pi-copy ml-3 text-500 text-xl cursor-pointer" @click="copy(get_link(bill.hash))"></span>
                                </span>
                            </div>
                        </div>

                        <span class="flex text-900 font-medium mb-2 xl:mb-6 text-xl xl:text-4xl lg:text-3xl md:text-2xl">
                            {{header[lang_].transfers_title}}
                        </span>

                        <DataTable v-if='transfers && total > 0' :value="transfers">
                            <Column field="_id" header="ID">
                                <template #body="{ data }">
                                    <span class="text-sm flex flex-row" @click="copy(data._id)">
                                        <span class="flex">{{get_short_id(data._id)}}</span>
                                        <span class="flex pi pi-copy ml-2 text-500 text-xs cursor-pointer"></span>
                                    </span>
                                </template>
                            </Column>
                            <Column field="status" :header="table[lang_].status">
                                <template #body="{ data }">
                                    <div :class="'status-'+data.status" 
                                        class="inline-block py-1 px-1 text-xs border-round-md border-2 surface-border text-white align-items-center white-space-nowrap" >
                                        <span class="flex text-xs">{{getStatusLabel(data.status)}}</span>
                                    </div>
                                </template>
                            </Column>
                            
                            <Column field="sender" :header="table[lang_].fullname">
                                <template #body="{ data }">
                                    <span class="text-sm" v-if="data.sender && data.sender.fullname">{{ data.sender.fullname }}</span>
                                </template>
                            </Column>

                            <Column field="sender" :header="table[lang_].description">
                                <template #body="{ data }">
                                    <span class="text-sm" v-if="data.sender && data.sender.description">{{ data.sender.description }}</span>
                                </template>
                            </Column>

                            <Column field="dates" :header="table[lang_].date">
                                <template #body="{ data }">
                                    <span class="text-sm" v-if="data.dates && data.dates.created">{{ formatDate(data.dates.created) }}</span>
                                </template>
                            </Column>

                        </DataTable>
                        <Skeleton v-if="!transfers && total==null" height="2rem" />
                        <span v-if="total==0">{{ content[lang_].zero_transfers }}</span>
                    </div>
                </div>
                <Skeleton height="2rem" v-else />
            </div>
        </template>
    </Page>
</template>

<script>
import Connect from '@/assets/js/Connect'
import Helper from '@/assets/js/Helper'
import { useRoute } from 'vue-router'
import Page from '@/components/sides/Page.vue'

export default {
    data() {
        return {
            header: {
                ru: {
                    name: "Счет",
                    transfers_title: 'Переводы по счету'
                },
                en: {
                    name: "Bill",
                    transfers_title: 'Bill`s transfers'
                },
            }, 
            content: {
                ru: {
                    recipient: 'Получатель',
                    amount: 'Сумма счета',
                    noAmount: 'Без указания суммы',
                    fullname: 'Имя получателя',
                    bank_code: 'Банк',
                    account_number: 'Номер счета',
                    phone_number: 'Номер телефона',
                    card_number: 'Номер карты',
                    wallet_address: 'Адрес кошелька',
                    wallet_network: 'Сеть кошелька',
                    telegram: 'Telegram',
                    description: 'Описание',
                    link: 'Ссылка на счет',
                    zero_transfers: 'По этому счету еще нет переводов',
                    copied: 'Скопировано',
                    error: 'Ошибка'
                },
                en: {
                    recipient: 'Recipient',
                    amount: 'Amount',
                    noAmount: 'No amount specified',
                    fullname: 'Fullname of recipient',
                    bank_code: 'Bank',
                    account_number: 'Account number',
                    phone_number: 'Phone number',
                    card_number: 'Card number',
                    wallet_address: 'Wallet address',
                    wallet_network: 'Wallet network',
                    telegram: 'Telegram',
                    description: 'Description',
                    link: 'Link',
                    zero_transfers: 'There are no transfers for this bill yet',
                    copied: 'Copied',
                    error: 'Error'
                },
            },
            table: {
                ru: {
                    status: 'Статус',
                    fullname: 'Имя плательщика',
                    description: 'Комментарий',
                    date: 'Дата'
                },
                en: {
                    status: 'Status',
                    fullname: 'Recipient`s name',
                    description: 'Description',
                    date: 'Date'
                },
            },
            bill_id: null,
            bill: null,
            transfers: null,
            total: null
        }
    },
    components: {
        Page
    },
    computed: {
        lang_() { return (this.$i18n.locale) ? this.$i18n.locale : 'ru' },
        isDarkTheme() { return this.$store.getters.StateDarkTheme },
    },
    methods: {
        get_format_amount(value, currency, digits) {
            return this.helper.format_amount(value, currency, digits)
        },
        get_bank_title(code){
            return this.helper.get_bank_title(code, this.$store.state.payment.banks, this.lang_)
        },
        formatDate(value) {
            return this.helper.format_date_short(value)
        },
        get_link(hash){
            if(!hash) return ''
            return this.connect.domain_url + '/bill/' + hash
        },
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: this.content[this.lang_].copied, detail: text, life: 1000 })
        },
        get_short_id(value){
            if(!value) return ''
            return this.helper.get_short_id(value)
        },
        getStatusLabel (status) {
            return this.helper.get_severity_status(status, this.lang_)
        },
    },
    async beforeCreate(){
        this.connect = new Connect()
        this.helper = new Helper()
        const route = useRoute()

        if(route.params.id && this.helper.is_id(route.params.id)) this.bill_id = route.params.id
        if(!this.bill_id) this.$router.push({ name: 'Not found' })

        const bill = await this.connect.get_bill_by_user(this.bill_id).then(res=>res)
        if(!bill.result || !bill.data) this.$router.push({ name: 'Not found' })

        // console.log('bill', bill)

        this.bill = bill.data
        const transfers = await this.connect.get_transfers_by_bill(route.params.id).then(res=>res)
        if(transfers.result && transfers.data) {
            // console.log('transfers', transfers)
            this.transfers = transfers.data
            this.total = transfers.total
        } else {
            this.transfers = []
            this.total = 0
        }
    },
    mounted(){
        this.helper.SetTgAppHeadColor('white', this.isDarkTheme)
        this.helper.SetTgAppBackButton(false)
    }
}
</script>